/**
 * The phone's side of the tunnel: turn a call into an envelope and wait.
 *
 * Several calls are in flight the moment a page loads, so every frame says
 * which request it belongs to and answers are handed back by that id rather
 * than by arrival order.
 *
 * A request resolves as soon as the answer's status and headers are known,
 * not when its body is complete. That is what lets a council answer be shown
 * token by token while it is still being written, and it is why the timeout
 * here covers the wait for a reply and not the length of the answer. A model
 * that thinks for two minutes is not a broken connection.
 */

import { TunnelError, cut, decodeFrame, encodeFrame } from "./tunnel-protocol";

/** Long enough for a desktop to answer, short enough to not hang a screen. */
export const REPLY_TIMEOUT_MS = 30000;

/**
 * @param channel  something with send(text) and onMessage(handler)
 * @param newId    injectable so tests are not at the mercy of randomness
 */
export function createTunnelClient({
  channel,
  timeoutMs = REPLY_TIMEOUT_MS,
  newId = () => Math.random().toString(36).slice(2),
  setTimeoutImpl = setTimeout,
  clearTimeoutImpl = clearTimeout,
  onPairing = null,
  onRendezvous = null,
  onPong = null,
}) {
  const waiting = new Map();

  const fail = (id, reason) => {
    const one = waiting.get(id);
    if (!one) return;
    waiting.delete(id);
    clearTimeoutImpl(one.timer);
    one.reject(new TunnelError(reason));
    one.spill(null, new TunnelError(reason));
  };

  const receive = (raw) => {
    let frame;
    try {
      frame = decodeFrame(raw);
    } catch {
      return; // not ours, or not readable: never a reason to take a screen down
    }
    if (frame.kind === "pairing") {
      // Not an answer to anything: the desktop offering to sign this phone
      // in. It arrives once, unasked, right after the channel opens.
      onPairing?.({ offer: frame.id, secret: frame.secret });
      return;
    }
    if (frame.kind === "rendezvous") {
      // Where to meet if this channel dies. Also unasked, and the reason a
      // phone that wakes up an hour later has somewhere to go.
      onRendezvous?.({ room: frame.id, secret: frame.secret });
      return;
    }
    if (frame.kind === "ping") {
      try {
        channel.send(encodeFrame({ kind: "pong", id: frame.id }));
      } catch {
        // Answering a ping on a channel that just died is not worth an error.
      }
      return;
    }
    if (frame.kind === "pong") {
      onPong?.(frame.id);
      return;
    }

    const one = waiting.get(frame.id);
    if (!one) return; // an answer to something already given up on

    if (frame.kind === "head") {
      clearTimeoutImpl(one.timer);
      one.resolve(one.answer(frame));
    } else if (frame.kind === "chunk") {
      one.spill(frame.data ?? "");
    } else if (frame.kind === "end") {
      waiting.delete(frame.id);
      one.spill(null);
    } else if (frame.kind === "fail") {
      fail(frame.id, frame.reason || "The other device could not do that.");
    }
  };

  channel.onMessage?.(receive);

  function request({ method = "GET", path, headers, body }) {
    const id = newId();

    // The body arrives in pieces. Whoever is reading gets them as they come;
    // whoever wants the whole thing waits for the end. Both from one list.
    const pieces = [];
    let listener = null;
    let closed = null;
    let whole = null;
    const spill = (piece, failure) => {
      if (failure) {
        closed = failure;
      } else if (piece === null) {
        closed = closed || true;
      } else {
        pieces.push(piece);
        listener?.(piece);
      }
      if (closed && whole) whole(closed === true ? pieces.join("") : null, closed);
    };

    const answer = (head) => ({
      status: head.status,
      headers: head.headers || {},
      /** Pieces as they arrive. For a council answer being written live. */
      onChunk(handler) {
        listener = handler;
        pieces.forEach(handler); // whatever already arrived
      },
      /** The whole body, once there is no more of it. */
      text() {
        return new Promise((resolve, reject) => {
          whole = (text, failure) =>
            failure && failure !== true ? reject(failure) : resolve(text ?? pieces.join(""));
          if (closed) whole(closed === true ? pieces.join("") : null, closed);
        });
      },
    });

    return new Promise((resolve, reject) => {
      const timer = setTimeoutImpl(
        () => fail(id, "Your other device did not answer in time."),
        timeoutMs
      );
      waiting.set(id, { resolve, reject, timer, spill, answer });

      try {
        channel.send(encodeFrame({ kind: "ask", id, method, path, headers, body }));
      } catch (e) {
        clearTimeoutImpl(timer);
        waiting.delete(id);
        reject(e instanceof TunnelError ? e : new TunnelError(e.message));
      }
    });
  }

  return {
    request,
    receive,
    get pending() {
      return waiting.size;
    },
    /** The channel went away. Everything still waiting has to be told. */
    stop(reason = "The connection to your other device is gone.") {
      [...waiting.keys()].forEach((id) => fail(id, reason));
    },
  };
}

/** Splitting a body for sending lives with the protocol; re-exported for use here. */
export { cut };
